/**
 * EditorFilePicker — модалка выбора редактируемого документа из «Редактора».
 *
 * Источник: GET /api/docs/list — он:
 *   • уже фильтрует по доступу пользователя (uploader или член drive-топика
 *     или drive-топик его отдела) через `_access_clause`;
 *   • исключает чужие DM (не-членам DM-документы не видны);
 *   • уже отдаёт только редактируемые типы (.docx/.xlsx/.pptx/.pdf/...).
 *
 * Возвращает { attachment_id } через onPick; родитель клонирует attachment
 * и кладёт в pendingFiles (см. ChatPage.onPick для drivePicker).
 *
 * Стиль — наш tg-style (повторно использует .drive-picker-* + .docs-filter-* классы).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Icon } from "@/components/prototype/Icon";
import { api } from "@/api/client";
import { Avatar } from "@/components/prototype/Avatar";

interface DocItem {
  attachment_id: number;
  filename: string;
  size_bytes: number | null;
  created_at: string | null;
  uploaded_by_name: string | null;
  uploaded_by_user_id: string | null;
  uploaded_by_avatar?: string | null;
  chat_topic_name: string | null;
  chat_topic_kind: string | null;
  drive_topic_name: string | null;
  drive_folder_name: string | null;
}

interface DocsListResp {
  items: DocItem[];
  total: number;
}

interface FilterOptions {
  departments: string[];
  chats: { id: number; name: string }[];
  users: { id: string; display_name: string; avatar_filename: string | null }[];
}

const DEPT_LABELS: Record<string, string> = {
  sales: "Отдел продаж",
  production: "Производство",
  marketing: "Маркетинг",
  admin: "Администрация",
  finance: "Финансы",
  supply: "Снабжение",
};

const DATE_PRESETS: { key: string; label: string; days?: number }[] = [
  { key: "any", label: "За всё время" },
  { key: "today", label: "Сегодня", days: 0 },
  { key: "week", label: "За неделю", days: 7 },
  { key: "month", label: "За месяц", days: 30 },
  { key: "quarter", label: "За 3 месяца", days: 90 },
];

interface Filters {
  q: string;
  department: string;
  chat_topic_id: number | null;
  uploaded_by: string;
  date_preset: string;
  sort: "recent" | "edited";
}

const EMPTY_FILTERS: Filters = {
  q: "", department: "", chat_topic_id: null,
  uploaded_by: "", date_preset: "any", sort: "recent",
};

function isoDateNDaysAgo(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 19).replace("T", " ");
}

interface Props {
  onPick: (file: { attachment_id: number; name: string }) => void;
  onClose: () => void;
}

function fmtSize(n: number | null): string {
  if (n == null) return "";
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} КБ`;
  return `${(n / (1024 * 1024)).toFixed(1)} МБ`;
}

function ext(filename: string): string {
  const p = filename.lastIndexOf(".");
  return p > 0 ? filename.slice(p + 1).toUpperCase() : "";
}

/* Локальная копия FilterDropdown из DocsView — чтобы не трогать тот файл. */
interface DropdownProps<T> {
  label: string;
  value: T | null;
  options: { value: T; label: string; sub?: string; left?: React.ReactNode }[];
  onChange: (v: T | null) => void;
  searchable?: boolean;
}

function FilterDropdown<T extends string | number>({
  label, value, options, onChange, searchable,
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter(o =>
      o.label.toLowerCase().includes(q) || (o.sub ?? "").toLowerCase().includes(q));
  }, [search, options]);

  const selected = options.find(o => o.value === value);

  return (
    <div className="docs-filter-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`docs-filter-chip${value !== null && value !== "" ? " is-active" : ""}`}
        onClick={() => setOpen(v => !v)}
      >
        <span className="docs-filter-chip-label">{label}{selected ? `: ${selected.label}` : ""}</span>
        {value !== null && value !== "" ? (
          <span
            className="docs-filter-chip-x"
            onClick={(e) => { e.stopPropagation(); onChange(null); setOpen(false); }}
            role="button"
            aria-label="Сбросить"
          >
            <Icon name="x" size={12}/>
          </span>
        ) : (
          <Icon name="chevron_d" size={12}/>
        )}
      </button>
      {open && (
        <div className="docs-filter-pop">
          {searchable && (
            <div className="docs-filter-pop-search">
              <Icon name="search" size={14}/>
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск…"
              />
            </div>
          )}
          <div className="docs-filter-pop-list">
            {filtered.length === 0 && (
              <div className="docs-filter-pop-empty">Ничего не найдено</div>
            )}
            {filtered.map(o => (
              <button
                key={String(o.value)}
                type="button"
                className={`docs-filter-pop-item${o.value === value ? " is-active" : ""}`}
                onClick={() => { onChange(o.value); setOpen(false); setSearch(""); }}
              >
                {o.left && <span className="docs-filter-pop-left">{o.left}</span>}
                <span className="docs-filter-pop-text">
                  <span>{o.label}</span>
                  {o.sub && <span className="docs-filter-pop-sub">{o.sub}</span>}
                </span>
                {o.value === value && <Icon name="check" size={14}/>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function EditorFilePicker({ onPick, onClose }: Props) {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [debouncedQ, setDebouncedQ] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q.trim()), 200);
    return () => clearTimeout(t);
  }, [filters.q]);

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const effectiveFilters = useMemo(
    () => ({ ...filters, q: debouncedQ }),
    [filters, debouncedQ]);

  const listQ = useQuery({
    queryKey: ["editor-picker/docs", effectiveFilters],
    queryFn: async (): Promise<DocsListResp> => {
      const f = effectiveFilters;
      const params = new URLSearchParams({ limit: "100" });
      if (f.q) params.set("q", f.q);
      if (f.department) params.set("department", f.department);
      if (f.chat_topic_id) params.set("chat_topic_id", String(f.chat_topic_id));
      if (f.uploaded_by) params.set("uploaded_by", f.uploaded_by);
      if (f.sort) params.set("sort", f.sort);
      const preset = DATE_PRESETS.find(p => p.key === f.date_preset);
      if (preset && typeof preset.days === "number") {
        params.set("date_from", isoDateNDaysAgo(preset.days));
      }
      return api(`/api/docs/list?${params}`);
    },
    staleTime: 30_000,
  });

  const optsQ = useQuery({
    queryKey: ["docs/filter-options"],
    queryFn: async (): Promise<FilterOptions> => api(`/api/docs/filter-options`),
    staleTime: 5 * 60_000,
  });

  const items = useMemo(() => listQ.data?.items ?? [], [listQ.data]);
  const opts = optsQ.data;
  const deptOptions = (opts?.departments ?? []).map(d => ({ value: d, label: DEPT_LABELS[d] ?? d }));
  const chatOptions = (opts?.chats ?? []).map(c => ({ value: c.id, label: c.name || "(без названия)" }));
  const userOptions = (opts?.users ?? []).map(u => ({
    value: u.id,
    label: u.display_name || "(без имени)",
    left: <Avatar name={u.display_name || "?"} userId={u.id} avatarFilename={u.avatar_filename ?? null} size={20}/>,
  }));

  const hasActive = filters.department || filters.chat_topic_id || filters.uploaded_by
    || filters.date_preset !== "any" || filters.sort !== "recent";

  return (
    <div className="drive-picker-modal drive-picker-inline editor-picker-modal" onMouseDown={(e) => e.stopPropagation()}>
      <div className="drive-picker-header">
        <div className="drive-picker-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Icon name="edit" size={16}/>
          <span>Файл из редактора</span>
        </div>
        <button type="button" className="drive-picker-close" onClick={onClose} aria-label="Закрыть">
          <Icon name="x" size={16}/>
        </button>
      </div>
      <div className="drive-picker-search-wrap">
        <Icon name="search" size={16}/>
        <input
          ref={inputRef}
          className="drive-picker-search"
          type="text"
          value={filters.q}
          onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
          placeholder="Поиск по имени файла"
        />
        {filters.q && (
          <button
            type="button"
            className="drive-picker-search-clear"
            onClick={() => setFilters(f => ({ ...f, q: "" }))}
            aria-label="Очистить"
          ><Icon name="x" size={12}/></button>
        )}
      </div>

      {/* === Фильтр-бар (как в /docs) === */}
      <div className="docs-filter-bar editor-picker-filter-bar">
        <button
          type="button"
          className={`docs-filter-tab${filters.sort === "recent" ? " is-active" : ""}`}
          onClick={() => setFilters(f => ({ ...f, sort: "recent" }))}
        >
          Все
        </button>
        <button
          type="button"
          className={`docs-filter-tab${filters.sort === "edited" ? " is-active" : ""}`}
          onClick={() => setFilters(f => ({ ...f, sort: "edited" }))}
          title="Документы, отредактированные через OnlyOffice (save-as-copy)"
        >
          Недавно отредактированные
        </button>
        <div className="docs-filter-divider"/>
        <FilterDropdown
          label="Отдел"
          value={filters.department || null}
          options={deptOptions}
          onChange={(v) => setFilters(f => ({ ...f, department: v ?? "" }))}
        />
        <FilterDropdown
          label="Чат"
          value={filters.chat_topic_id}
          options={chatOptions}
          onChange={(v) => setFilters(f => ({ ...f, chat_topic_id: v }))}
          searchable
        />
        <FilterDropdown
          label="Автор"
          value={filters.uploaded_by || null}
          options={userOptions}
          onChange={(v) => setFilters(f => ({ ...f, uploaded_by: v ?? "" }))}
          searchable
        />
        <FilterDropdown
          label="Дата"
          value={filters.date_preset === "any" ? null : filters.date_preset}
          options={DATE_PRESETS.filter(p => p.key !== "any").map(p => ({ value: p.key, label: p.label }))}
          onChange={(v) => setFilters(f => ({ ...f, date_preset: v ?? "any" }))}
        />
        {hasActive && (
          <button
            type="button"
            className="docs-filter-reset"
            onClick={() => setFilters({ ...EMPTY_FILTERS, q: filters.q })}
            title="Сбросить все фильтры"
          >Сбросить</button>
        )}
      </div>

      <div className="drive-picker-body">
        {listQ.isLoading && <div className="drive-picker-loading">Загрузка…</div>}
        {listQ.error && <div className="drive-picker-empty">Ошибка: {(listQ.error as Error).message}</div>}
        {!listQ.isLoading && !listQ.error && items.length === 0 && (
          <div className="drive-picker-empty">
            {debouncedQ || hasActive ? "Ничего не найдено" : "Нет доступных вам документов"}
          </div>
        )}
        {!listQ.isLoading && items.length > 0 && (
          <div className="drive-picker-content editor-picker-list">
            {items.map(it => {
              const source = it.drive_folder_name
                ? `${it.drive_topic_name ?? ""} › ${it.drive_folder_name}`
                : it.chat_topic_name ?? "";
              const isDm = it.chat_topic_kind === "dm";
              return (
                <button
                  key={it.attachment_id}
                  type="button"
                  className="drive-picker-row drive-picker-row-file editor-picker-row"
                  onClick={() => onPick({ attachment_id: it.attachment_id, name: it.filename })}
                >
                  <span className="editor-picker-ext">{ext(it.filename) || "FILE"}</span>
                  <span className="editor-picker-main">
                    <span className="editor-picker-name">{it.filename}</span>
                    <span className="editor-picker-meta">
                      {fmtSize(it.size_bytes)}
                      {it.uploaded_by_name && <> · {it.uploaded_by_name}</>}
                      {source && <> · <span className="editor-picker-source">{source}</span></>}
                      {isDm && <span className="editor-picker-dm-badge">DM</span>}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
